import { z } from 'zod';
import { zData } from './agenda.js';
import { zCentavos, zId } from './primitivos.js';
/**
 * Os contratos de estoque e comandas — `#v-bar`, `#m-comanda`,
 * `#v-estoque`, `#m-produto`, `#m-inventario` e a aba Comandas de
 * `#m-aluno`.
 *
 * Quatro regras moram aqui porque os dois lados precisam delas iguais:
 *
 *   quanto se ganha            `margemDoProduto` — a coluna Margem de
 *                              `#v-estoque` e o "Margem de 54%" que
 *                              `#m-produto` mostra ANTES de gravar
 *   o que está acabando        `situacaoDoProduto` — a coluna Situação e
 *                              o alerta de estoque mínimo do documento 18
 *   quanto pedir               `reposicaoSugerida` — o card "Reposição
 *                              sugerida", `minimo * 2 - saldo`
 *   quanto a comanda deve      `totalDaComanda` — o total ao vivo de
 *                              `#m-comanda`, que o fechamento recalcula
 *                              no servidor
 *
 * E as de sempre: dinheiro em CENTAVOS, percentual em CENTÉSIMOS de ponto
 * (5429 = 54,29%), e nada de float.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------
/**
 * A coluna Categoria de `#v-estoque`. O protótipo usa duas — Bebida e
 * Alimento —; `Outro` existe para o guarda-sol e a bola de aluguel não
 * obrigarem uma migration no dia em que a arena resolver vendê-los.
 */
export const CATEGORIA_PRODUTO = ['Bebida', 'Alimento', 'Outro'];
export const ROTULO_TIPO_MOVIMENTO = {
    Entrada: 'Entrada de mercadoria',
    SaidaPorVenda: 'Venda',
    AjusteInventario: 'Ajuste de inventário',
    Perda: 'Perda',
    EstornoDeVenda: 'Estorno de venda',
    EstornoDeEntrada: 'Estorno de entrada',
};
/**
 * As quatro formas de fechamento de `#m-comanda`.
 *
 * `Mensalidade` é a que torna o módulo interessante: não entra dinheiro
 * agora, o valor vai para a fatura aberta do aluno. As outras três são
 * caixa na hora.
 */
export const FORMAS_DE_FECHAMENTO = [
    'Pix',
    'Dinheiro',
    'Cartao',
    'Mensalidade',
];
export const ROTULO_FORMA_DE_FECHAMENTO = {
    Pix: 'Pix',
    Dinheiro: 'Dinheiro',
    Cartao: 'Cartão',
    Mensalidade: 'Lançar na mensalidade',
};
/** Quem paga agora — as três que produzem lançamento no razão ao fechar. */
export function fechamentoEmDinheiro(forma) {
    return forma !== 'Mensalidade';
}
/**
 * A situação da linha em `#v-estoque`, e a fonte do alerta do documento 18.
 *
 *   Critico   no mínimo ou abaixo dele — é o "Coca-Cola Zero abaixo do
 *             mínimo · 4 unidades, mínimo 12"
 *   Baixo     a menos de 50% acima do mínimo: ainda dá tempo de pedir
 *   Ok        o resto
 */
export const SITUACAO_PRODUTO = ['Critico', 'Baixo', 'Ok'];
export const ROTULO_SITUACAO_PRODUTO = {
    Critico: 'Abaixo do mínimo',
    Baixo: 'Pouco estoque',
    Ok: 'Normal',
};
// ---------------------------------------------------------------------
// As regras que os dois lados calculam igual
// ---------------------------------------------------------------------
/**
 * `(preco - custo) / preco`, em centésimos de ponto.
 *
 * CALCULADA, nunca gravada: custo e preço mudam, e uma coluna `margem`
 * gravada fica errada no minuto seguinte sem ninguém perceber. Produto
 * sem preço não tem margem — devolve `null` em vez de dividir por zero.
 */
export function margemDoProduto(custo, preco) {
    if (preco <= 0)
        return null;
    return Math.round(((preco - custo) / preco) * 10000);
}
/** "54%" — o rótulo da coluna, a partir dos centésimos de ponto. */
export function margemPorExtenso(margem) {
    if (margem === null)
        return '—';
    return `${Math.round(margem / 100)}%`;
}
export function situacaoDoProduto(saldo, minimo) {
    if (saldo <= minimo)
        return 'Critico';
    if (saldo <= Math.ceil(minimo * 1.5))
        return 'Baixo';
    return 'Ok';
}
/**
 * Quanto pedir ao fornecedor: `minimo * 2 - saldo`, a heurística de ponto
 * de pedido do protótipo — repor até o dobro do mínimo. Nunca negativo.
 */
export function reposicaoSugerida(saldo, minimo) {
    return Math.max(minimo * 2 - saldo, 0);
}
/** O total ao vivo de `#m-comanda`. O preço é o do ITEM, já congelado. */
export function totalDaComanda(itens) {
    return itens.reduce((soma, item) => soma + item.quantidade * item.precoUnitario, 0);
}
/** "A entrada gera saída de R$ 153,60" — 48 unidades a R$ 3,20. */
export function custoDaEntrada(quantidade, custoUnitario) {
    return quantidade * custoUnitario;
}
// ---------------------------------------------------------------------
// Limites
// ---------------------------------------------------------------------
/**
 * Tetos de sanidade, não de negócio. Existem para que um dedo escorregado
 * no teclado ("4800 unidades") pare na validação e não no estoque.
 */
export const QUANTIDADE_MAXIMA_POR_MOVIMENTO = 10_000;
export const PRECO_MAXIMO_DO_PRODUTO = 1_000_00;
export const ITENS_MAXIMOS_POR_COMANDA = 100;
// ---------------------------------------------------------------------
// Custo médio · a entrada de mercadoria
// ---------------------------------------------------------------------
/**
 * O custo do produto é o MÉDIO PONDERADO das compras que ainda estão na
 * prateleira: `(saldo·custo + qtd·compra) / (saldo + qtd)`, em centavos.
 *
 * `saldoAntes` é o saldo ANTES da entrada. Negativo conta como zero: o que
 * se vendeu sem estar no sistema não tem custo para diluir a compra nova, e
 * deixá-lo entrar com sinal puxaria o médio para fora do intervalo
 * [custoAtual, compra]. Sem saldo, o custo é o da compra.
 *
 * Argumentos: `custoAtual`, `quantidade` e `custoUnitario` são inteiros não
 * negativos, com `quantidade >= 1`. `saldoAntes` é inteiro e PODE ser
 * negativo — é o caso do produto vendido sem estar no sistema —, e então
 * conta como zero.
 */
export function custoMedio(saldoAntes, custoAtual, quantidade, custoUnitario) {
    const base = Math.max(saldoAntes, 0);
    return Math.round((base * custoAtual + quantidade * custoUnitario) / (base + quantidade));
}
/**
 * O inverso de `custoMedio`: o custo que o produto tinha ANTES de uma
 * entrada, para o estorno dela. `saldoAtual` é o saldo de agora, que ainda
 * inclui a entrada.
 *
 * Sem saldo anterior (`saldoAtual − quantidade <= 0`) não há o que
 * recuperar, e um resultado fora de [0, R$ 1.000,00] é sinal de que o
 * saldo andou depois da entrada e a conta não fecha mais: nos dois casos o
 * custo fica como está. Errar para o lado de não mexer é o seguro: a
 * próxima compra o reequilibra.
 */
export function custoMedioSemAEntrada(saldoAtual, custoAtual, quantidade, custoUnitario) {
    const saldoAntes = saldoAtual - quantidade;
    if (saldoAntes <= 0)
        return custoAtual;
    const anterior = Math.round((saldoAtual * custoAtual - quantidade * custoUnitario) / saldoAntes);
    if (anterior < 0 || anterior > PRECO_MAXIMO_DO_PRODUTO)
        return custoAtual;
    // `Math.round` devolve -0 para um quociente entre -0,5 e 0; `|| 0` o normaliza.
    return anterior || 0;
}
// ---------------------------------------------------------------------
// Produtos · #m-produto e #v-estoque
// ---------------------------------------------------------------------
const zNomeDeProduto = z.string().trim().min(2, 'Informe o nome do produto.').max(80);
export const zProdutoNovo = z.object({
    nome: zNomeDeProduto,
    categoria: z.enum(CATEGORIA_PRODUTO),
    /** Centavos. Zero é legítimo: o brinde que baixa estoque sem receita. */
    custo: zCentavos.max(PRECO_MAXIMO_DO_PRODUTO),
    preco: zCentavos.max(PRECO_MAXIMO_DO_PRODUTO),
    estoqueMinimo: z.number().int().min(0).max(QUANTIDADE_MAXIMA_POR_MOVIMENTO),
});
/**
 * Edição parcial: `#m-produto` reaproveita o formulário para os dois.
 *
 * SEM `custo`: o custo muda só por entrada de mercadoria (é o médio
 * ponderado), nunca por edição. Um cliente antigo que ainda o envie não leva
 * erro — o zod descarta a chave desconhecida — mas o custo não muda.
 */
export const zProdutoEdicao = zProdutoNovo.omit({ custo: true }).partial().extend({
    ativo: z.boolean().optional(),
});
/**
 * "Digite parte do nome: coca, agua, acai..." — a busca de `#m-comanda`.
 * Um caractere já basta: a lista de produtos de uma arena é curta, e
 * exigir três atrapalha quem digita "gelo".
 */
export const zBuscaDeProduto = z.object({
    termo: z.string().trim().min(1).max(60),
    // Vem na query string, sempre como texto ("8"): sem `coerce`, a busca
    // do app real era recusada com 400 a cada tecla.
    limite: z.coerce.number().int().min(1).max(20).default(8),
});
// ---------------------------------------------------------------------
// Entrada de mercadoria · #m-produto
// ---------------------------------------------------------------------
export const zFornecedorNovo = z.object({
    nome: z.string().trim().min(2, 'Informe o fornecedor.').max(80),
    contato: z.string().trim().max(120).optional(),
});
/**
 * O produto que nasce junto com a entrada: o cadastro de `#m-produto` MENOS
 * o custo. A primeira compra É o custo do produto novo, e é o
 * `custoUnitario` do item — dizê-lo duas vezes abriria espaço para dois
 * números diferentes.
 */
export const zProdutoNovoDaEntrada = zProdutoNovo.omit({ custo: true });
/**
 * Um item da entrada de `#m-produto`: um produto que JÁ EXISTE (`produtoId`)
 * OU um que nasce com ela (`produtoNovo`) — nunca os dois, nunca nenhum. É a
 * mesma forma de `zAberturaDeComanda` (aluno OU nome avulso), e pelo mesmo
 * motivo: a mensagem de um `union` do zod seria "Invalid input".
 *
 * `preco` e `estoqueMinimo` do item são do produto JÁ CADASTRADO: o modal os
 * edita na mesma tela — quem repõe costuma ser quem descobre que o preço
 * subiu — e, omitidos, o produto fica como está. O produto novo traz o preço
 * e o mínimo dele dentro de `produtoNovo`, e os do item ao lado seriam um
 * segundo valor para a mesma coisa.
 */
export const zItemDaEntrada = z
    .object({
    produtoId: zId.optional(),
    produtoNovo: zProdutoNovoDaEntrada.optional(),
    quantidade: z.number().int().min(1).max(QUANTIDADE_MAXIMA_POR_MOVIMENTO),
    custoUnitario: zCentavos.max(PRECO_MAXIMO_DO_PRODUTO),
    preco: zCentavos.max(PRECO_MAXIMO_DO_PRODUTO).optional(),
    estoqueMinimo: z.number().int().min(0).max(QUANTIDADE_MAXIMA_POR_MOVIMENTO).optional(),
})
    .superRefine((item, ctx) => {
    const temId = item.produtoId !== undefined;
    const temNovo = item.produtoNovo !== undefined;
    if (temId === temNovo) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: temId
                ? 'Informe o produto já cadastrado (produtoId) OU um produto novo (produtoNovo), nunca os dois.'
                : 'Cada item precisa de um produto: o já cadastrado (produtoId) ou um novo (produtoNovo).',
        });
        return;
    }
    if (temNovo) {
        for (const campo of ['preco', 'estoqueMinimo']) {
            if (item[campo] === undefined)
                continue;
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [campo],
                message: 'O preço e o estoque mínimo do produto novo vão dentro de produtoNovo.',
            });
        }
    }
});
/** As formas com que se paga um fornecedor. */
export const FORMAS_DE_PAGAMENTO_DA_COMPRA = ['Pix', 'Dinheiro', 'Cartao', 'Boleto'];
/**
 * A entrada de `#m-produto`: um fornecedor, uma nota, um ou mais produtos —
 * já cadastrados ou novos. Gera UMA saída no razão, com a soma de todos.
 */
export const zEntradaDeMercadoria = z
    .object({
    fornecedorId: zId,
    /** O número da nota, quando existe — é o que concilia com o financeiro. */
    nota: z.string().trim().max(40).optional(),
    itens: z.array(zItemDaEntrada).min(1, 'A entrada precisa de pelo menos um produto.').max(50),
    /** Como a compra é (ou será) paga. A `Mensalidade` não é forma de pagar fornecedor. */
    formaPagamento: z.enum(FORMAS_DE_PAGAMENTO_DA_COMPRA).default('Pix'),
    /**
     * O dia em que a compra vence. Depois de hoje (no calendário da arena), a
     * saída nasce PREVISTA nesse dia e é paga com "realizar" no financeiro;
     * sem ele, ou hoje ou antes, a saída nasce realizada hoje.
     */
    vencimento: zData.optional(),
})
    // O custo médio é calculado produto a produto contra o saldo que o banco
    // trava; duas linhas do mesmo produto na mesma entrada dariam dois
    // cálculos sobre o mesmo saldo. A checagem olha só os itens que têm
    // `produtoId`: dois produtos NOVOS com o mesmo nome não têm id para
    // comparar, e quem os recusa é o índice único de nome (409). Maiúscula e
    // minúscula são o mesmo UUID.
    .refine((v) => {
    const ids = v.itens.flatMap((item) => (item.produtoId ? [item.produtoId.toLowerCase()] : []));
    return new Set(ids).size === ids.length;
}, {
    path: ['itens'],
    message: 'O mesmo produto aparece mais de uma vez na entrada. Some as quantidades.',
});
/**
 * `GET /estoque/entradas` — o histórico de compras, da mais recente para
 * trás. Uma página por vez, por cursor: um deslocamento repetiria linha
 * quando outra entrada fosse registrada durante a leitura.
 */
export const zConsultaDeEntradas = z.object({
    /** Opaco: o servidor o monta e só ele o lê. */
    cursor: z.string().max(200).optional(),
    // Vem na query string, sempre como texto ("25"): sem `coerce`, a tela era
    // recusada com 400 — a mesma lição da busca de produto.
    limite: z.coerce.number().int().min(1).max(100).default(25),
});
/**
 * `GET /estoque/produtos/:id/movimentos` e `GET /estoque/inventarios` — as
 * duas páginas por cursor têm a mesma consulta que as entradas.
 */
export const zConsultaDePagina = zConsultaDeEntradas;
// ---------------------------------------------------------------------
// Inventário · #m-inventario
// ---------------------------------------------------------------------
export const zItemDoInventario = z.object({
    produtoId: zId,
    /** A contagem física. O saldo do sistema é lido no servidor, não enviado. */
    contado: z.number().int().min(0).max(QUANTIDADE_MAXIMA_POR_MOVIMENTO),
});
/**
 * A justificativa é OBRIGATÓRIA quando há divergência — regra do
 * documento, checada no servidor (que é quem conhece o saldo) e repetida
 * aqui pelo formulário, que já sabe a diferença que está exibindo.
 */
export const zInventario = z
    .object({
    itens: z.array(zItemDoInventario).min(1).max(200),
    justificativa: z.string().trim().max(400).optional(),
    /**
     * O que o formulário calculou. Não é o dado de gravação — o servidor
     * recalcula —, serve só para exigir a justificativa no cliente sem
     * uma ida ao servidor.
     */
    haDivergencia: z.boolean().optional(),
})
    .refine((v) => !v.haDivergencia || (v.justificativa?.length ?? 0) >= 3, {
    path: ['justificativa'],
    message: 'Contagem diferente do sistema exige justificativa.',
});
// ---------------------------------------------------------------------
// Comandas · #v-bar e #m-comanda
// ---------------------------------------------------------------------
/**
 * Abrir comanda: para um ALUNO ou para um nome avulso, nunca os dois.
 *
 * "Breno em locação avulsa" e "Diego avulso pagando no Pix" são os dois
 * casos do protótipo — gente sem cadastro consome no bar, e recusar isso
 * empurraria a venda para fora do sistema.
 */
export const zAberturaDeComanda = z
    .object({
    alunoId: zId.optional(),
    nomeAvulso: z.string().trim().min(2).max(60).optional(),
    observacao: z.string().trim().max(120).optional(),
})
    .refine((v) => (v.alunoId ? 1 : 0) + (v.nomeAvulso ? 1 : 0) === 1, {
    message: 'Informe o aluno OU um nome avulso.',
});
/**
 * `GET /comandas` — o histórico: as FECHADAS ou as CANCELADAS, nunca as
 * abertas (essas têm a própria rota, que é a tela do balcão).
 *
 * `de` e `ate` são dias do calendário DA ARENA, inclusive nos dois lados;
 * o servidor os converte em instantes pelo fuso dela. `forma` só existe
 * para as fechadas: a cancelada não foi paga, e aceitar o filtro devolveria
 * uma lista vazia que parece resposta.
 *
 * Tudo chega como texto, por ser query string — daí o `coerce` no limite,
 * a mesma lição da busca de produto.
 */
export const zConsultaDeComandas = z
    .object({
    situacao: z.enum(['fechadas', 'canceladas']),
    de: zData.optional(),
    ate: zData.optional(),
    forma: z.enum(FORMAS_DE_FECHAMENTO).optional(),
    /** Opaco: o servidor o monta e só ele o lê. */
    cursor: z.string().max(200).optional(),
    limite: z.coerce.number().int().min(1).max(100).default(25),
})
    .refine((v) => !v.de || !v.ate || v.de <= v.ate, {
    path: ['ate'],
    message: 'A data final vem antes da inicial.',
})
    .refine((v) => !v.forma || v.situacao === 'fechadas', {
    path: ['forma'],
    message: 'Só comanda fechada tem forma de pagamento.',
});
export const zLancamentoDeItem = z.object({
    produtoId: zId,
    quantidade: z.number().int().min(1).max(QUANTIDADE_MAXIMA_POR_MOVIMENTO).default(1),
});
/** O nome que o cartão de `#v-bar` mostra: o aluno, ou o avulso. */
export function nomeNaComanda(comanda) {
    return comanda.aluno?.nome ?? comanda.nomeAvulso ?? 'Sem nome';
}
export const zFechamentoDeComanda = z.object({
    forma: z.enum(FORMAS_DE_FECHAMENTO),
    /**
     * O total que a tela estava mostrando. O servidor recalcula e RECUSA se
     * divergir: é a proteção contra fechar uma comanda que ganhou um item
     * em outro terminal entre a conferência e o clique.
     */
    totalConferido: zCentavos.optional(),
});
/**
 * Os limites do motivo de quem cancela uma comanda aberta ou estorna uma
 * fechada — o servidor e a tela leem os mesmos números.
 */
export const MOTIVO_DE_COMANDA = { minimo: 3, maximo: 200 };
/** O corpo do cancelamento e do estorno de comanda: só o motivo. */
export const zCancelamentoDeComanda = z.object({
    motivo: z.string().trim().min(MOTIVO_DE_COMANDA.minimo).max(MOTIVO_DE_COMANDA.maximo),
});
/** O corpo do estorno de uma entrada de mercadoria: só o motivo, com os limites dos outros estornos. */
export const zEstornoDeEntrada = zCancelamentoDeComanda;
//# sourceMappingURL=estoque.js.map